/**
 * Normalisation report collector (W1-07a formats-json-rpe): folds repeated findings into one ImportReportEntry
 * with a `count`, tracks whether Phira itself would load the source (`phiraWouldLoad`) and collects the data that
 * moves to the phiedit.json sidecar (DESIGN.md §6.13, §7.2).
 *
 * Folding key: report class, message key, line index and the path with list indices removed, so "300 notes of
 * line 3 have alpha 255.0" is one entry (first path kept, count 300) instead of 300.
 */

import type { ImportClass, ImportReportEntry } from '../../core/ir/types';
import type { Json, MessageRef } from '../../core/util/types';

/** Data normalisation removed from the chart that only the phiedit.json sidecar keeps (LineRpeData.ignored). */
export interface RpeSidecarEntry {
  /** Index of the line in judgeLineList. */
  readonly line: number;
  /** Where the data was in the line, e.g. "extended.gifEvents" or "eventLayers[2].alphaEvents". */
  readonly key: string;
  readonly value: Json;
}

interface Folded {
  readonly cls: ImportClass;
  readonly message: MessageRef;
  readonly path: string | undefined;
  readonly line: number | undefined;
  count: number;
}

const INDEX_RE = /\[\d+\]/g;

/** Line index of a path such as "judgeLineList[3].notes[2]" (undefined when the path is not inside a line). */
export function lineOfPath(path: string): number | undefined {
  const m = /^judgeLineList\[(\d+)\]/.exec(path);
  return m === null ? undefined : Number(m[1]);
}

export class NormReport {
  private readonly folded = new Map<string, Folded>();
  private readonly order: string[] = [];
  private loads = true;
  readonly sidecar: RpeSidecarEntry[] = [];

  /** Records a finding (folded with equal ones). `count` adds that many occurrences at once. */
  add(cls: ImportClass, message: MessageRef, path?: string, line?: number, count = 1): void {
    const ln = line ?? (path === undefined ? undefined : lineOfPath(path));
    const pattern = path === undefined ? '' : path.replace(INDEX_RE, '[]');
    const key = `${cls}|${message.key}|${ln === undefined ? '' : String(ln)}|${pattern}`;
    const prev = this.folded.get(key);
    if (prev !== undefined) {
      prev.count += count;
      return;
    }
    this.folded.set(key, { cls, message, path, line: ln, count });
    this.order.push(key);
  }

  /** Phira would not load the source (parse failure, panic or refusal we repaired). */
  phiraWouldNotLoad(): void {
    this.loads = false;
  }

  get phiraWouldLoad(): boolean {
    return this.loads;
  }

  toSidecar(line: number, key: string, value: Json): void {
    this.sidecar.push({ line, key, value });
  }

  entries(): ImportReportEntry[] {
    const out: ImportReportEntry[] = [];
    for (const key of this.order) {
      const f = this.folded.get(key);
      if (f === undefined) continue;
      const entry: { -readonly [K in keyof ImportReportEntry]: ImportReportEntry[K] } = {
        cls: f.cls,
        message: f.message,
      };
      if (f.path !== undefined) entry.path = f.path;
      if (f.line !== undefined) entry.line = f.line;
      if (f.count > 1) entry.count = f.count;
      out.push(entry);
    }
    return out;
  }
}
